<template>
  <hs-modal
    :model-value="modelValue"
    title="Resolve inventory shortages"
    kicker="Inventory control"
    description="Apply received stock to an outstanding shortage."
    size="lg"
    @update:modelValue="close"
  >
    <div v-if="loading" class="reconcile-empty">
      <div class="reconcile-spinner" />
      <span>Loading shortages</span>
    </div>

    <div v-else-if="error" class="reconcile-error">
      {{ error }}
      <hs-button variant="secondary" icon="refresh-cw" @click="loadDeficits">
        Retry
      </hs-button>
    </div>

    <div v-else-if="!deficits.length" class="reconcile-empty">
      <div class="reconcile-empty__icon">
        <vue-feather type="check-circle" size="26" />
      </div>
      <strong>No open shortages</strong>
      <span>All tracked stock commitments are covered.</span>
    </div>

    <div v-else class="reconcile-layout">
      <section class="reconcile-section">
        <div class="reconcile-section__heading">
          <span>Open shortages</span>
          <small>{{ deficits.length }}</small>
        </div>
        <div class="reconcile-shortage-list">
          <button
            v-for="deficit in deficits"
            :key="deficit.commitment_line_id"
            type="button"
            class="reconcile-shortage"
            :class="{ 'reconcile-shortage--selected': String(deficit.commitment_line_id) === selectedLineId }"
            @click="selectDeficit(deficit)"
          >
            <span class="reconcile-shortage__icon">
              <vue-feather type="alert-triangle" size="16" />
            </span>
            <span class="reconcile-shortage__body">
              <strong>{{ deficit.product?.name || 'Inventory item' }}</strong>
              <small>{{ formatQuantity(deficit.deficit_quantity) }} {{ unitFor(deficit) }} short</small>
            </span>
            <span class="reconcile-shortage__amount">
              {{ formatQuantity(deficit.deficit_quantity) }}
            </span>
          </button>
        </div>
      </section>

      <section v-if="selectedDeficit" class="reconcile-section reconcile-section--form">
        <div class="reconcile-section__heading">
          <span>Apply received stock</span>
        </div>

        <div class="reconcile-product-summary">
          <div class="reconcile-product-summary__icon">
            <vue-feather type="package" size="18" />
          </div>
          <div>
            <strong>{{ selectedDeficit.product?.name }}</strong>
            <span>{{ formatQuantity(selectedDeficit.deficit_quantity) }} {{ unitFor(selectedDeficit) }} to resolve</span>
          </div>
        </div>

        <hs-select
          v-model="selectedPurchaseId"
          label="Received stock batch"
          placeholder="Choose received stock"
          icon="package"
          :options="purchaseOptions"
          :disabled="!purchaseOptions.length || saving"
          :hint="purchaseOptions.length ? 'Only stock not already held for another ticket is shown.' : 'Receive stock for this item before reconciling.'"
          @change="applyBatchDefaults"
        />

        <hs-input
          v-model="reconcileQuantity"
          type="number"
          min="0.01"
          step="0.01"
          label="Quantity to apply"
          icon="hash"
          :disabled="!selectedBatch || saving"
          :error="quantityError"
          :hint="quantityHint"
        />

        <hs-textarea
          v-model="note"
          label="Reconciliation note"
          placeholder="e.g. Minibar delivery received and applied to room service shortage"
          :disabled="saving"
          :rows="3"
        />
      </section>
    </div>

    <template #footer>
      <hs-button variant="secondary" :disabled="saving" @click="close">
        Close
      </hs-button>
      <hs-button
        v-if="selectedDeficit"
        variant="primary"
        icon="check-circle"
        :loading="saving"
        :disabled="!canReconcile"
        @click="reconcile"
      >
        Apply stock
      </hs-button>
    </template>
  </hs-modal>
</template>

<script>
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import api from "@/helper/api";
import { HsButton, HsInput, HsModal, HsSelect, HsTextarea } from "@/components/ui";

export default {
  name: "InventoryReconciliationModal",
  components: {
    HsButton,
    HsInput,
    HsModal,
    HsSelect,
    HsTextarea,
  },
  props: {
    modelValue: { type: Boolean, default: false },
    storeId: { type: [Number, String], default: null },
  },
  emits: ["update:modelValue", "close", "reconciled"],
  data() {
    return {
      deficits: [],
      selectedLineId: "",
      selectedPurchaseId: "",
      reconcileQuantity: "",
      note: "",
      loading: false,
      saving: false,
      error: "",
    };
  },
  computed: {
    selectedDeficit() {
      return this.deficits.find(
        (deficit) => String(deficit.commitment_line_id) === String(this.selectedLineId),
      ) || null;
    },
    purchaseOptions() {
      return (this.selectedDeficit?.reconciliation_options || []).map((option) => ({
        value: String(option.product_purchase_id),
        label: this.purchaseLabel(option),
      }));
    },
    selectedBatch() {
      return (this.selectedDeficit?.reconciliation_options || []).find(
        (option) => String(option.product_purchase_id) === String(this.selectedPurchaseId),
      ) || null;
    },
    maxQuantity() {
      if (!this.selectedDeficit || !this.selectedBatch) return 0;
      return Math.min(
        Number(this.selectedDeficit.deficit_quantity || 0),
        Number(this.selectedBatch.available_quantity || 0),
      );
    },
    quantityNumber() {
      const value = Number(this.reconcileQuantity);
      return Number.isFinite(value) ? value : 0;
    },
    quantityError() {
      if (!this.reconcileQuantity || !this.selectedBatch) return "";
      if (this.quantityNumber <= 0) return "Enter a quantity greater than zero.";
      if (this.quantityNumber > this.maxQuantity) {
        return `Up to ${this.formatQuantity(this.maxQuantity)} ${this.unitFor(this.selectedDeficit)} can be applied.`;
      }
      return "";
    },
    quantityHint() {
      if (!this.selectedBatch) return "Choose a received batch first.";
      return `${this.formatQuantity(this.selectedBatch.available_quantity)} ${this.unitFor(this.selectedDeficit)} available in this batch.`;
    },
    canReconcile() {
      return Boolean(
        this.selectedDeficit
        && this.selectedBatch
        && this.quantityNumber > 0
        && this.quantityNumber <= this.maxQuantity
        && !this.saving,
      );
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.loadDeficits();
    },
    storeId() {
      if (this.modelValue) this.loadDeficits();
    },
  },
  methods: {
    async loadDeficits() {
      if (!this.storeId) return;
      this.loading = true;
      this.error = "";
      try {
        const response = await http.get(api.inventory_commitment_deficits(this.storeId));
        this.deficits = response?.data?.items || [];
        const current = this.deficits.find(
          (deficit) => String(deficit.commitment_line_id) === String(this.selectedLineId),
        );
        if (current) this.selectDeficit(current, { preserveQuantity: true });
        else if (this.deficits.length) this.selectDeficit(this.deficits[0]);
        else this.resetSelection();
      } catch (error) {
        this.error = error?.response?.data?.message || "Unable to load inventory shortages.";
      } finally {
        this.loading = false;
      }
    },
    selectDeficit(deficit, { preserveQuantity = false } = {}) {
      this.selectedLineId = String(deficit.commitment_line_id);
      this.selectedPurchaseId = String(
        deficit.reconciliation_options?.[0]?.product_purchase_id || "",
      );
      if (!preserveQuantity) this.note = "";
      this.applyBatchDefaults({ preserveQuantity });
    },
    resetSelection() {
      this.selectedLineId = "";
      this.selectedPurchaseId = "";
      this.reconcileQuantity = "";
      this.note = "";
    },
    applyBatchDefaults({ preserveQuantity = false } = {}) {
      if (!this.selectedBatch) {
        this.reconcileQuantity = "";
        return;
      }
      if (!preserveQuantity || this.quantityNumber > this.maxQuantity) {
        this.reconcileQuantity = String(this.maxQuantity || "");
      }
    },
    async reconcile() {
      if (!this.canReconcile) return;
      this.saving = true;
      try {
        const response = await http.post(
          api.inventory_commitment_reconcile(
            this.storeId,
            this.selectedDeficit.commitment_line_id,
          ),
          {
            product_purchase_id: Number(this.selectedPurchaseId),
            quantity: this.quantityNumber,
            note: this.note.trim() || null,
          },
        );
        toast.success("Received stock applied to the shortage");
        this.$emit("reconciled", response.data);
        await this.loadDeficits();
      } catch (error) {
        const message = error?.response?.data?.message || "Unable to reconcile this shortage.";
        toast.error(message);
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.$emit("update:modelValue", false);
      this.$emit("close");
    },
    formatQuantity(value) {
      const numeric = Number(value || 0);
      return numeric.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    unitFor(deficit) {
      return String(
        deficit?.quantity_unit_code
        || deficit?.base_quantity_unit
        || "unit",
      ).toLowerCase();
    },
    purchaseLabel(option) {
      const supplier = option.supplier_name || "Received stock";
      const receivedAt = option.received_at
        ? new Intl.DateTimeFormat(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(new Date(option.received_at))
        : "Recent receipt";
      const price = `${option.currency_code || "RWF"} ${this.formatQuantity(option.unit_price)}`;
      return `${supplier} · ${receivedAt} · ${this.formatQuantity(option.available_quantity)} available · ${price}`;
    },
  },
};
</script>

<style scoped>
.reconcile-layout {
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
}

.reconcile-section {
  background: color-mix(in srgb, var(--hs-bg, #f8fafc) 72%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 18px;
  display: grid;
  gap: 0.85rem;
  min-width: 0;
  padding: 0.95rem;
}

.reconcile-section--form {
  align-content: start;
}

.reconcile-section__heading {
  align-items: center;
  color: var(--hs-text, #0f172a);
  display: flex;
  font-size: 0.78rem;
  font-weight: 950;
  justify-content: space-between;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.reconcile-section__heading small {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent);
  border-radius: 999px;
  color: var(--hs-primary, #3293ec);
  display: inline-flex;
  font-size: 0.72rem;
  min-height: 22px;
  padding: 0 8px;
}

.reconcile-shortage-list {
  display: grid;
  gap: 0.55rem;
  max-height: 330px;
  overflow: auto;
}

.reconcile-shortage {
  align-items: center;
  background: var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  cursor: pointer;
  display: grid;
  gap: 0.65rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 0.7rem;
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.reconcile-shortage:hover,
.reconcile-shortage--selected {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 60%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 8px 18px color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
  transform: translateY(-1px);
}

.reconcile-shortage__icon,
.reconcile-product-summary__icon,
.reconcile-empty__icon {
  align-items: center;
  background: rgba(239, 68, 68, 0.11);
  border-radius: 11px;
  color: #dc2626;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.reconcile-shortage__body {
  display: grid;
  gap: 0.2rem;
  min-width: 0;
}

.reconcile-shortage__body strong,
.reconcile-product-summary strong {
  color: var(--hs-text, #0f172a);
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reconcile-shortage__body small,
.reconcile-product-summary span {
  color: var(--hs-muted, #64748b);
  font-size: 0.73rem;
  font-weight: 800;
}

.reconcile-shortage__amount {
  color: #dc2626;
  font-size: 0.86rem;
  font-weight: 950;
}

.reconcile-product-summary {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 8%, transparent);
  border-radius: 15px;
  display: flex;
  gap: 0.7rem;
  padding: 0.75rem;
}

.reconcile-product-summary__icon {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 14%, transparent);
  color: var(--hs-primary, #3293ec);
}

.reconcile-product-summary > div:last-child {
  display: grid;
  gap: 0.15rem;
  min-width: 0;
}

.reconcile-empty,
.reconcile-error {
  align-items: center;
  color: var(--hs-muted, #64748b);
  display: grid;
  font-size: 0.86rem;
  font-weight: 800;
  justify-items: center;
  min-height: 210px;
  text-align: center;
}

.reconcile-empty {
  gap: 0.55rem;
}

.reconcile-empty strong {
  color: var(--hs-text, #0f172a);
}

.reconcile-empty__icon {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
  height: 46px;
  width: 46px;
}

.reconcile-error {
  color: #b91c1c;
  gap: 1rem;
}

.reconcile-spinner {
  animation: reconcile-spin 0.8s linear infinite;
  border: 3px solid color-mix(in srgb, var(--hs-primary, #3293ec) 20%, transparent);
  border-radius: 50%;
  border-top-color: var(--hs-primary, #3293ec);
  height: 24px;
  width: 24px;
}

@keyframes reconcile-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 760px) {
  .reconcile-layout {
    grid-template-columns: 1fr;
  }

  .reconcile-shortage-list {
    max-height: 210px;
  }
}
</style>
